/**
 * Death and respawn lifecycle.
 *
 *  - Player collision with a non-stunned enemy -> phase 'dying',
 *    respawnAt = tick + dyingDuration.
 *  - Each tick in 'dying' counts down. When tick >= respawnAt:
 *      - lives > 1: lose a life, respawn at maze.spawn, phase 'playing',
 *        fuel reset, enemies reset to spawn positions.
 *      - lives <= 1: lose a life (to 0), phase 'gameover'.
 */

import type { GameEvent, GameState, InputFrame } from './types.ts';
import { toTile } from './fixed.ts';
import { atTileCenter, committedDirection, spawnCar } from './movement.ts';
import { createEnemies } from './enemies.ts';
import { dirVec } from './direction.ts';
import { isBoulder } from './maze.ts';
import { fuelForStage } from './stage.ts';

/**
 * Triggers a death when the player rolls up to a tile center committed to a
 * direction whose next tile holds a boulder. The committed direction respects
 * this tick's input, so a player who reverses or turns away at the same
 * instant they reach the center isn't killed for their previous heading.
 *
 * Walls and other dead-ends don't qualify — only boulder impact moments do.
 */
export function checkPlayerBoulderCollision(state: GameState, input: InputFrame): GameState {
  if (state.phase !== 'playing') return state;
  if (!atTileCenter(state.player)) return state;
  const dir = committedDirection(state.player, input, state.maze);
  if (dir === null) return state;
  const tx = toTile(state.player.x);
  const ty = toTile(state.player.y);
  const v = dirVec(dir);
  if (!isBoulder(state.maze, tx + v.dx, ty + v.dy)) return state;
  return {
    ...state,
    phase: 'dying',
    deathCause: 'crash',
    respawnAt: state.tick + state.config.dyingDuration,
    events: [...state.events, { kind: 'player_die' }],
  };
}

export function checkPlayerEnemyCollision(state: GameState): GameState {
  if (state.phase !== 'playing') return state;
  const ptx = toTile(state.player.x);
  const pty = toTile(state.player.y);
  const hit = state.enemies.some((e) => {
    if (state.tick < e.stunUntil) return false;
    return toTile(e.car.x) === ptx && toTile(e.car.y) === pty;
  });
  if (!hit) return state;
  return {
    ...state,
    phase: 'dying',
    deathCause: 'crash',
    respawnAt: state.tick + state.config.dyingDuration,
    events: [...state.events, { kind: 'player_die' }],
  };
}

export function tickDying(state: GameState): GameState {
  if (state.phase !== 'dying') return state;
  if (state.tick < state.respawnAt) return state;

  const livesAfter = state.lives - 1;
  if (livesAfter <= 0) {
    return {
      ...state,
      lives: 0,
      phase: 'gameover',
      events: [...state.events, { kind: 'game_over' }],
    };
  }

  const events: GameEvent[] = [...state.events];
  return {
    ...state,
    lives: livesAfter,
    phase: 'playing',
    deathCause: null,
    player: spawnCar(state.maze.spawn.x, state.maze.spawn.y),
    enemies: createEnemies(state.maze, state.enemies.length, state.stage),
    fuel: fuelForStage(state.stage, state.config),
    smokes: [],
    events,
  };
}
